/**
 * API Client for ChartZen Web
 * Replaces Electron IPC calls with HTTP fetch calls
 */

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Fetch wrapper with credentials and error handling
 */
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * API client organized by domain
 */
export const api = {
  /**
   * Authentication endpoints
   */
  auth: {
    /**
     * Get the OAuth login URL (redirects to Google)
     */
    getLoginUrl: () => `${API_URL}/api/oauth/login`,

    /**
     * Logout - clears session
     */
    logout: () =>
      fetchAPI<{ success: boolean }>('/api/oauth/logout', { method: 'POST' }),

    /**
     * Get current session info
     */
    getSession: () =>
      fetchAPI<{
        authenticated: boolean;
        user?: {
          id: string;
          email: string;
          name: string | null;
          picture: string | null;
        };
        googleConnection?: {
          id: string;
          email: string;
          isValid: boolean;
        } | null;
        properties?: Array<{
          id: string;
          name: string;
          accountId: string;
          ga4Property?: {
            id: string;
            displayName: string;
          };
          gscSite?: {
            siteUrl: string;
            permissionLevel?: string;
          };
        }>;
      }>('/api/oauth/session'),
  },

  /**
   * Google API proxy endpoints
   */
  google: {
    /**
     * Get cached properties
     */
    getProperties: () =>
      fetchAPI<{
        properties: Array<{
          id: string;
          name: string;
          accountId: string;
          ga4Property?: {
            id: string;
            displayName: string;
          };
          gscSite?: {
            siteUrl: string;
            permissionLevel?: string;
          };
        }>;
      }>('/api/google/properties'),

    /**
     * Refresh properties from Google APIs
     */
    refreshProperties: () =>
      fetchAPI<{
        properties: Array<{
          id: string;
          name: string;
          accountId: string;
          ga4Property?: {
            id: string;
            displayName: string;
          };
          gscSite?: {
            siteUrl: string;
            permissionLevel?: string;
          };
        }>;
      }>('/api/google/properties/refresh', { method: 'POST' }),

    /**
     * Run a GA4 report
     */
    runGAReport: (request: {
      propertyId: string;
      dimensions?: string[];
      metrics: string[];
      dateRange?: { startDate: string; endDate: string };
      dimensionFilter?: any;
      orderBy?: {
        metric?: string;
        dimension?: string;
        desc?: boolean;
      };
      limit?: number;
      cohortSpec?: any;
    }) =>
      fetchAPI<any>('/api/google/ga/report', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    /**
     * Run a GSC query
     */
    runGSCQuery: (request: {
      siteUrl: string;
      dimensions?: string[];
      startDate: string;
      endDate: string;
      filters?: Array<{
        dimension: string;
        operator: string;
        expression: string;
      }>;
      rowLimit?: number;
    }) =>
      fetchAPI<any>('/api/google/gsc/query', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
  },

  /**
   * AI endpoints
   */
  ai: {
    /**
     * Get available AI models
     */
    getModels: () =>
      fetchAPI<{
        models: Array<{
          id: string;
          name: string;
          provider: string;
          available: boolean;
        }>;
        default: string;
      }>('/api/ai/models'),

    /**
     * Send a query to the AI
     */
    query: (request: {
      query: string;
      context: {
        propertyId: string;
        siteUrl?: string;
        dateRange?: {
          startDate: string;
          endDate: string;
        };
      };
      conversationId?: string;
      model?: string;
    }) =>
      fetchAPI<{
        conversationId: string;
        model: string;
        response?: string;
        toolCalls?: Array<{
          id: string;
          name: string;
          parameters: Record<string, any>;
        }>;
        usage?: {
          inputTokens: number;
          outputTokens: number;
        };
      }>('/api/ai/query', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    /**
     * Continue conversation with tool results
     */
    continue: (request: {
      conversationId: string;
      toolResults: Array<{
        toolCallId: string;
        result: any;
      }>;
    }) =>
      fetchAPI<{
        conversationId: string;
        model: string;
        response?: string;
        toolCalls?: Array<{
          id: string;
          name: string;
          parameters: Record<string, any>;
        }>;
        usage?: {
          inputTokens: number;
          outputTokens: number;
        };
      }>('/api/ai/continue', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
  },
};

export default api;
